import { motion } from "framer-motion";
import { Network, Smartphone, LayoutDashboard, Workflow } from "lucide-react";
import { useLang } from "@/lib/i18n";

const CAP_META = [
  { id: "agents", Icon: Network },
  { id: "mobile", Icon: Smartphone },
  { id: "ops", Icon: LayoutDashboard },
  { id: "workflows", Icon: Workflow },
];

export const Capabilities = () => {
  const { t } = useLang();

  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative isolate overflow-hidden bg-[color:var(--ivory)] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <div className="mb-14 flex items-baseline gap-6">
          <span className="eyebrow">{t.caps.eyebrow}</span>
          <div className="hairline-rule flex-1" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true, margin: "-100px" }}
          data-testid="capabilities-headline"
          className="font-display max-w-[18ch] text-[30px] leading-[1.08] text-[color:var(--ink)] sm:text-[38px] lg:text-[48px]"
        >
          {t.caps.title_a}{" "}
          <span className="font-serif-accent italic text-[color:var(--emerald)]">
            {t.caps.title_b}
          </span>
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 gap-x-14 gap-y-14 md:grid-cols-2">
          {CAP_META.map(({ id, Icon }, i) => {
            const card = t.caps.cards[id];
            return (
              <motion.article
                key={id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.06 * i }}
                viewport={{ once: true, margin: "-80px" }}
                data-testid={`capability-card-${id}`}
                className="group border-t border-[color:var(--rule-strong)] pt-6"
              >
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[color:var(--emerald)]/[0.08] ring-1 ring-[color:var(--emerald)]/25">
                    <Icon
                      className="h-4 w-4 text-[color:var(--emerald)]"
                      strokeWidth={1.75}
                    />
                  </div>
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display mt-5 text-[22px] leading-tight text-[color:var(--ink)] sm:text-[26px]">
                  {card.title}
                </h3>
                <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[color:var(--ink-3)]">
                  {card.desc}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
